import { useLocalStorage } from "@/hooks/useLocalStorage";
import { BG_OPTIONS } from "@/components/preview/RemotionPreview";

/**
 * Preview-appearance section of a Settings tab — the BG picker and the
 * content-scale slider relocated from the preview toolbar. Reads/writes the
 * exact same localStorage slots the toolbar used (motionforge-preview-bg and
 * the content_scale field of motionforge-render-settings), so the preview and
 * the render pipeline stay in lockstep no matter which surface edited them.
 *
 * Shared by TemplateDetail's Settings tab and PromptWorkspace's Settings tab —
 * both drive the same RemotionPreview chrome via those localStorage slots.
 */
export function PreviewAppearanceSection() {
  const [bg, setBg] = useLocalStorage<string>(
    "motionforge-preview-bg",
    "checker",
  );
  const [renderSettings, setRenderSettings] = useLocalStorage<{
    content_scale?: number;
    [k: string]: unknown;
  }>("motionforge-render-settings", {});
  const contentScale = renderSettings.content_scale ?? 1;
  const setContentScale = (v: number) =>
    setRenderSettings((prev) => ({ ...prev, content_scale: v }));

  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-ink">Preview appearance</h3>
      {/* Background — segmented picker over the shared BG options. */}
      <div className="flex flex-col gap-2 text-xs text-ink-2">
        <label className="text-xs font-medium text-ink-2">Background</label>
        <div className="flex flex-wrap gap-1.5">
          {BG_OPTIONS.map((opt) => {
            const active = opt.value === bg;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setBg(opt.value)}
                className={`rounded-md px-2.5 py-1 text-xs transition-colors ${
                  active
                    ? "bg-accent-soft text-accent"
                    : "bg-surface-3 text-ink-2 hover:text-ink"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      {/* Content scale — same 0.3–1.0 slider, same render-settings slot. */}
      <div className="flex flex-col gap-2 text-xs text-ink-2">
        <label className="text-xs font-medium text-ink-2">
          Content scale
        </label>
        <div
          className="flex items-center gap-2"
          title="Content scale — shrinks the graphic to fit overflowing text. Linked to render settings."
        >
          <input
            type="range"
            min={0.3}
            max={1}
            step={0.05}
            value={contentScale}
            onChange={(e) => setContentScale(parseFloat(e.target.value))}
            className="accent-accent"
          />
          <span className="w-10 text-right text-ink-2">
            {Math.round(contentScale * 100)}%
          </span>
        </div>
      </div>
    </section>
  );
}
